import { useCallback, useState } from "react";
import { Box, Text } from "@mantine/core";
import { EditableInfoRowContext } from "./EditableInfoRowContext";
import {
  type RowAlign,
  type RowVariant,
  rowTextStyle,
} from "./DocumentHeaderStyles";

interface EditableInfoRowProps {
  label: string;
  displayValue: string;
  isReadOnly?: boolean;
  editor: React.ReactNode;
  minWidth?: number;
  variant?: RowVariant;
  align?: RowAlign;
}

export function EditableInfoRow({
  label,
  displayValue,
  isReadOnly,
  editor,
  minWidth = 82,
  variant = "row",
  align = "left",
}: EditableInfoRowProps) {
  const [editing, setEditing] = useState(false);
  const [localOverride, setLocalOverride] = useState<string | null>(null);
  const shownValue = localOverride ?? displayValue;
  const baseStyle = rowTextStyle(variant);
  const labelStyle: React.CSSProperties = {
    ...baseStyle,
    textTransform: "uppercase",
    flexShrink: 0,
    ...(align === "left" ? { minWidth } : {}),
  };
  const containerStyle: React.CSSProperties = {
    display: "flex",
    gap: 6,
    alignItems: "baseline",
    ...(align === "right" ? { justifyContent: "space-between" } : {}),
  };
  const handleBlur = useCallback(() => setEditing(false), []);
  const handleClick = useCallback(() => setEditing(true), []);

  if (isReadOnly) {
    return (
      <Box style={containerStyle}>
        <Text style={labelStyle}>{label}:</Text>
        <Text style={baseStyle}>{shownValue || "—"}</Text>
      </Box>
    );
  }

  return (
    <EditableInfoRowContext.Provider value={setLocalOverride}>
      <Box style={containerStyle}>
        <Text style={labelStyle}>{label}:</Text>
        {editing ? (
          <Box
            style={align === "right" ? undefined : { flex: 1 }}
            onBlur={handleBlur}
          >
            {editor}
          </Box>
        ) : (
          <Text
            onClick={handleClick}
            style={{
              ...baseStyle,
              cursor: "pointer",
              borderRadius: 3,
              padding: "1px 4px",
              marginLeft: -4,
              minHeight: 14,
            }}
            className="header-editable-value"
          >
            {shownValue || (
              <span style={{ color: "#aaa", fontStyle: "italic" }}>—</span>
            )}
          </Text>
        )}
      </Box>
    </EditableInfoRowContext.Provider>
  );
}
